import { createSlice } from "@reduxjs/toolkit";
import { throttle } from "../../utils/throttle";
import { syncOrchestrator } from "../../api/services/syncOrchestrator";

// Optional: keep this in sync with your orchestrator's exclude rules if you want
const EXCLUDE_KEYS = new Set([
  // auth/session
  "authToken",
  "refreshToken",
  "syncToken",
  // runtime & telemetry
  "lastLocalChange",
  "lastServerUpdatedAt",
  "lastSyncTime",
  "userCredentials",
  "deviceId",
  "EXIT_RESUME_TOKEN",
]);

let listenersInstalled = false;
let removeFns = [];
let throttledForce = null;

const lsSyncWatcherSlice = createSlice({
  name: "lsSyncWatcher",
  initialState: {
    enabled: false,
    lastReason: null,
    lastEventAt: null,
  },
  reducers: {
    _setEnabled(state, action) {
      state.enabled = action.payload;
    },
    _setLast(state, action) {
      state.lastReason = action.payload?.reason || null;
      state.lastEventAt = Date.now();
    },
  },
});

export const { _setEnabled, _setLast } = lsSyncWatcherSlice.actions;
export default lsSyncWatcherSlice.reducer;

/** Helper: are we allowed to sync right now? */
function isAuthed(getState) {
  const s = getState?.() ?? {};
  const a = s.auth ?? {};
  // Redux flag OR token presence; also ensure orchestrator not disabled
  const hasToken =
    !!a.token ||
    !!a.accessToken ||
    !!a.isAuthenticated ||
    !!localStorage.getItem("authToken");
  return !!hasToken && !syncOrchestrator.disabled;
}

/**
 * Start watching LS changes and immediately sync (throttled) — but ONLY if authed.
 * @param {{ throttleMs?: number, reason?: string }} opts
 */
export const startLocalStorageSyncWatcher =
  (opts = {}) =>
  (dispatch, getState) => {
    const { throttleMs = 3000, reason = "watcher-start" } = opts;

    // If not authenticated, make sure watchers are NOT installed and bail.
    if (!isAuthed(getState)) {
      if (listenersInstalled) {
        // hard stop if something left them running
        dispatch(stopLocalStorageSyncWatcher());
      } else {
        dispatch(_setEnabled(false));
      }
      return;
    }

    if (listenersInstalled) {
      dispatch(_setEnabled(true));
      dispatch(_setLast({ reason: "already-installed" }));
      return;
    }

    // Throttled force that double-checks auth & orchestrator state each time
    throttledForce = throttle((why) => {
      if (!isAuthed(getState)) return; // ← gate every fire
      if (syncOrchestrator.disabled) return; // ← circuit breaker after 401
      syncOrchestrator.markDirty();
      syncOrchestrator.forceSyncNow(why);
      dispatch(_setLast({ reason: why }));
    }, throttleMs);

    // 1) Custom event from same tab changes
    const onDirty = () => {
      if (!isAuthed(getState)) return;
      throttledForce("WTUFO_DIRTY");
    };
    window.addEventListener("WTUFO_DIRTY", onDirty);
    removeFns.push(() => window.removeEventListener("WTUFO_DIRTY", onDirty));

    // 2) Native 'storage' event from other tabs
    const onStorage = (e) => {
      if (!isAuthed(getState)) return;
      const key = e.key;
      if (key && EXCLUDE_KEYS.has(key)) return;
      throttledForce("storage");
    };
    window.addEventListener("storage", onStorage);
    removeFns.push(() => window.removeEventListener("storage", onStorage));

    // 3) When tab becomes visible, push pending quickly
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!isAuthed(getState)) return;
      throttledForce("visible");
    };
    document.addEventListener("visibilitychange", onVisible);
    removeFns.push(() =>
      document.removeEventListener("visibilitychange", onVisible)
    );

    listenersInstalled = true;
    dispatch(_setEnabled(true));

    // Kick an initial (gated) sync
    throttledForce(reason);
  };

/** Stop watching entirely */
export const stopLocalStorageSyncWatcher = () => (dispatch) => {
  if (!listenersInstalled) {
    dispatch(_setEnabled(false));
    return;
  }
  removeFns.forEach((fn) => {
    try {
      fn();
    } catch {}
  });
  listenersInstalled = false;
  removeFns = [];
  throttledForce = null;
  dispatch(_setEnabled(false));
};
